// Packages
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from "react-router-dom";

// Helpers
import useHelpers from '../../../../helpers';

// Controllers
import useControllers from '../../../'

// Models
import useModels from '../../../../models';

// Actions
import useApi from "../../../../api";

const useUsersCreate = () => {
  const navigation = useNavigate();
  const [showModal, setShowModal] = useState(false)
  const [listRoles, setListRoles] = useState([])
  const [listCountries, setListCountries] = useState([]);
  const [activateUser, setActivateUser] = useState("Inactivo")
  const [groupedPermissions, setGroupedPermissions] = useState([]);

  // Controllers
  const {useGeneralHooks} = useControllers()
  const {formatDate} = useGeneralHooks()

  // Models
  const { useSelectors } = useModels();
  const { useSelector, useAuthSelectors } = useSelectors();
  const { session } = useAuthSelectors();
  const { rol: currentUserRole } = useSelector(session);

  // Helpers
  const { useQuickFunctions } = useHelpers();
  const { useValidators } = useQuickFunctions();
  const { newUserValidationShema } = useValidators();

  // Actions
  const { useActions } = useApi();
  const { useUsersActions, useGeneralActions } = useActions();
  const { actAddUsers, actListRoles } = useUsersActions();
  const { actListCountries, actListPermissions } = useGeneralActions();

  const {
    control,
    handleSubmit,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(newUserValidationShema),
  });

  const handleCloseModal = () => {
    setShowModal(false)
    navigation(-1)
  }

  const handleChangeActivate = () => {
    setActivateUser(activateUser === "Inactivo" ? "Activo" : "Inactivo")
  }

  const handleShowModal = () => {
    setShowModal(true)
  }
  
  const onSubmit = (data) => {
    // Format permissions correctly for API
    const formattedData = {
      ...data,
      country_id: data.country_id.map(c => c.value),
      active: activateUser === "Activo",
      birth_date: formatDate(data.birth_date, "Y-mm-dd", false),
      permissions: data.permissions || [] // Already in correct format: [{id, type}]
    };
    actAddUsers(formattedData, handleShowModal);
  };

  const loadDatarol = data => {
    setListRoles(data.map(v => {return {label: v.name, value: v.id}}))
  }
  const prepareDataCountry = (data) => {
    // Show all countries - not filtered by user's assigned countries
    // Only Súper Administrador and Administrador can edit this field (disabled in view)
    setListCountries(data.map(v => ({label: v.name, value: v.id})));
  };

  const loadPermissions = (data) => {
    // Group permissions the same way as roles
    const permissionsWithChildren = data.filter(perm => 
      data.some(other => other.key.startsWith(perm.key + '.'))
    );
    
    const parentKeys = new Set(permissionsWithChildren.map(p => p.key));
    const childKeys = new Set();
    data.forEach(perm => {
      const parts = perm.key.split('.');
      if (parts.length > 1) {
        childKeys.add(perm.key);
      }
    });
    
    const grouped = [];
    
    permissionsWithChildren.forEach(parent => {
      const children = data.filter(perm => {
        const parts = perm.key.split('.');
        const parentParts = parent.key.split('.');
        return perm.key.startsWith(parent.key + '.') && 
               parts.length === parentParts.length + 1;
      });
      
      if (children.length > 0) {
        grouped.push({
          parent: { label: parent.name, value: parent.id, key: parent.key },
          children: children.map(child => ({ 
            label: child.name, 
            value: child.id,
            key: child.key 
          }))
        });
      }
    });
    
    const standalonePermissions = data.filter(perm => 
      !parentKeys.has(perm.key) && !childKeys.has(perm.key)
    );
    
    standalonePermissions.forEach(standalone => {
      grouped.push({
        parent: { label: standalone.name, value: standalone.id, key: standalone.key },
        children: []
      });
    });

    setGroupedPermissions(grouped);
  };

  useEffect(() => {
    actListRoles(loadDatarol)
    actListCountries(prepareDataCountry);
    actListPermissions(loadPermissions);
  }, [])

  return {
    control,
    handleSubmit,
    onSubmit,
    showModal,
    handleCloseModal,
    activateUser,
    handleChangeActivate,
    listRoles,
    listCountries,
    groupedPermissions,
    currentUserRole,
  };
};

export default useUsersCreate;
